<script setup lang="ts">
import type { VehicleTypeInteger } from '@yescapa-dev/ysc-api-js/legacy'
import { LOCALE_CODES } from '@yescapa-dev/ysc-components/i18n'
import { OWNER_ONBOARDING_1 } from '~/constants/pictures'

const { getReferentialOptions } = useReferentialStore()
const { localeProperties: { yscCode } } = useLocaleProperties()

interface Form {
  type: string | null
  country: string
}

const form = ref<Form>({
  type: null,
  country: getTopLevelLocale(yscCode).toUpperCase(),
})

const vehicleTypeChoices = computed(() => {
  return getReferentialOptions('vehicle_type_choices').map(({ text, value }) => {
    return {
      text,
      value: value.toString(),
    }
  })
})

const { params } = storeToRefs(useEarningsStore())
const onSubmitEarning = () => {
  if (form.value.type) {
    params.value.vehicle_type = +form.value.type as VehicleTypeInteger // FIXME: form.type should be a number and not a string with a modifier on the input
  }
  params.value.vehicle_registration_country = form.value.country
  return navigateTo({ name: 'earnings-owner' })
}

const { locale } = useI18n()
</script>

<template>
  <div class="md:container flex justify-between items-center w-full flex-col gap-2 lg:mt-12 lg:gap-20 lg:flex-row">
    <div class="lg:w-3/5">
      <i18n-t
        keypath="components.app_rent_my_motorhome.header.title_dynamic"
        tag="h1"
        scope="global"
        class="px-4 md:px-0 md:max-w-full text-3xl font-semibold p-4 mb-1 lg:text-5xl lg:p-0 lg:mb-10"
      >
        <template #emphasis_string>
          <strong class="text-pink-500">{{ $t('components.app_rent_my_motorhome.header.title_emphasis') }}</strong>
        </template>
      </i18n-t>
      <div class="bg-gray-50 p-4 lg:p-6 lg:border lg:rounded">
        <p class="lg:text-xl font-semibold">
          {{ $t('components.app_rent_my_motorhome.header.simulation_description') }}
        </p>
        <YscForm
          id="rent-my-motorhome-earning-form"
          class="bg-white border border-gray-200 mx-auto rounded flex flex-col items-center justify-between p-4 mt-3"
        >
          <div class="flex flex-col flex-wrap justify-between space-y-4 grow w-full md:flex-row md:space-y-0 xl:flex-nowrap xl:justify-start">
            <YscInputSelectSearchSingle
              id="country"
              v-model="form.country"
              :options="getReferentialOptions('countries_main')"
              required
              full-width
              class="w-full md:w-5/12 xl:w-auto text-sm xl:text-base grow"
            >
              <span class="font-semibold">{{ $t('commons.location') }}</span>
            </YscInputSelectSearchSingle>
            <YscDivider
              is-vertical
              class="hidden md:block mx-3"
            />
            <YscInputSelectSearchSingle
              id="vehicle_type_select"
              v-model="form.type"
              :options="vehicleTypeChoices"
              :placeholder="$t('commons.actions.select')"
              required
              full-width
              class="w-full md:w-5/12 xl:w-auto text-sm xl:text-base grow"
            >
              <span class="font-semibold text-nowrap">{{ $t('components.app_search.filters.vehicle_type') }}</span>
            </YscInputSelectSearchSingle>
          </div>
          <YscButtonSubmit
            class="btn btn-primary rounded mt-3 w-full text-sm xl:text-base"
            @click="onSubmitEarning"
          >
            {{ $t('components.app_rent_my_motorhome.header.simulation_action') }}
          </YscButtonSubmit>
        </YscForm>
        <p class="text-gray-400 mt-4 text-xs md:text-sm">
          {{ $t('components.app_rent_my_motorhome.header.disclaimer') }}
        </p>
      </div>
    </div>
    <div class="px-4 md:px-0 md:max-w-full flex flex-col justify-center items-center mt-2 lg:mt-0 lg:w-2/5">
      <div class="rounded overflow-hidden w-full mr-0">
        <YscPicture
          :path="OWNER_ONBOARDING_1"
          :sources="[
            { breakpoint: 'xl', width: 500, height: 500 },
            { breakpoint: 'lg', width: 290, height: 290 },
            { breakpoint: 'md', width: 700, height: 700 / 16 * 9 },
          ]"
          with-cover-transformation
          :default-source="{ width: 200, height: 200 / 16 * 9 }"
          alt="-"
          mode="cover"
          fetchpriority="high"
          loading="eager"
        />
      </div>
      <div class="flex flex-col sm:flex-row justify-center mt-6 sm:mt-3 gap-6 w-full items-center">
        <AppRatingYescapa :text-align="locale === LOCALE_CODES.DE ? 'left' : 'center'" />
        <img
          v-if="locale === LOCALE_CODES.DE"
          src="~assets/img/awards/DKA2023_large.png"
          class="block w-60 sm:w-32 object-contain"
          alt="-"
          loading="lazy"
        >
      </div>
    </div>
  </div>
</template>
